<template>
  <!-- Ionic Icons -->
  <IonIcon
    v-if="iconConfig.type === 'ionic'"
    :icon="iconConfig.data"
    :color="ionicColor"
    :style="ionicStyle"
    v-bind="$attrs"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  />

  <!-- Font Awesome Icons -->
  <FontAwesomeIcon
    v-else-if="iconConfig.type === 'fontawesome'"
    :icon="iconConfig.data"
    :style="{ fontSize: sizeValue, color: currentColor }"
    v-bind="$attrs"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  />

  <!-- Heroicons or Custom SVG -->
  <img
    v-else-if="iconConfig.type === 'heroicons' || iconConfig.type === 'custom'"
    :src="iconConfig.data"
    :alt="name || 'icon'"
    :style="{ width: sizeValue, height: sizeValue, color: currentColor }"
    v-bind="$attrs"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  />

  <!-- Lottie Animation -->
  <div v-else-if="iconType === 'lottie'">
    <div
      ref="lottieContainer"
      :style="containerStyle"
      @click="handleLottieClick"
      @mouseenter="handleLottieMouseEnter"
      @mouseleave="handleLottieMouseLeave"
    />
  </div>
</template>

<script setup lang="ts">
import { IonIcon } from '@ionic/vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { AnimationItem } from 'lottie-web';
import * as lottie from 'lottie-web';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { getIconByName, getIconConfigSize, getIconType } from '../../../utils/iconUtils';

// Handle both ESM and CommonJS module formats
// const lottie = ((lottieModule as any).default || lottieModule) as LottiePlayer;

interface Props {
  name?: string;
  size?: string;
  animatedIconSize?: string; // Pour les icônes animées
  color?: string; // Couleurs Ionic: 'primary', 'secondary', 'danger', etc.
  cssColor?: string; // Couleurs CSS: 'var(--ion-color-primary)', '#FF0000', 'rgb(255,0,0)', etc.
  hoverColor?: string; // Couleur Ionic au hover
  hoverCssColor?: string; // Couleur CSS au hover
  activeColor?: string; // Couleur Ionic quand actif
  activeCssColor?: string; // Couleur CSS quand actif
  isActive?: boolean; // État actif de l'icône
  autoplay?: boolean;
  loop?: boolean;
  type?: 'ionic' | 'fontawesome' | 'heroicons' | 'custom' | 'lottie' | 'animatedicon.co';
  data?: any;
  // Nouvelles props pour les animations
  animateOnClick?: boolean;
  animateOnHover?: boolean;
  animationType?: 'play' | 'reverse' | 'segments';
  hoverAnimationType?: 'play' | 'reverse' | 'segments';
  segments?: [number, number]; // Pour l'animation par segments
  hoverSegments?: [number, number]; // Pour l'animation au hover
  clickable?: boolean;
  // Nouvelle prop pour déclencher l'animation automatiquement
  animationTrigger?: boolean | string | number;
}
// Pour le debug

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  animatedIconSize: '32px',
  autoplay: true,
  loop: true,
  animateOnClick: false,
  animateOnHover: false,
  animationType: 'play',
  hoverAnimationType: 'reverse',
  clickable: false,
  isActive: false,
});

const emit = defineEmits<{
  click: [event: MouseEvent];
  mouseenter: [event: MouseEvent];
  mouseleave: [event: MouseEvent];
  animationComplete: [];
  animationStart: [];
}>();

// Enable attribute inheritance for all child elements
defineOptions({
  inheritAttrs: false,
});

const lottieContainer = ref<HTMLElement>();
let animationInstance: AnimationItem | null = null;

// Ref réactive pour détecter les changements de thème
const isDarkTheme = ref(document.body.classList.contains('dark'));

// État de hover pour la gestion des couleurs
const isHovered = ref(false);

// Variable pour gérer la direction de l'animation reverse
const animationDirection = ref(1);
const hoverAnimationDirection = ref(1);

const iconConfig = computed(() => getIconByName(props.name));
const iconType = computed(() => getIconType(props.name));

const sizeValue = computed(() => {
  const sizeMap: Record<string, string> = {
    verySmall: '14px',
    small: '20px',
    medium: '28px',
    large: '32px',
    'extra-large': '48px',
  };
  return sizeMap[props.size] || props.size;
});

// Liste des couleurs Ionic valides
const ionicColors = [
  'primary',
  'secondary',
  'tertiary',
  'success',
  'warning',
  'danger',
  'light',
  'medium',
  'dark',
];

// Vérifie si la couleur est une couleur Ionic prédéfinie
const isIonicColor = (color: string | undefined): boolean => {
  return !!color && ionicColors.includes(color);
};

// Couleur actuelle basée sur l'état de hover
// Priorité: hoverCssColor > hoverColor > cssColor > color > default
const currentColor = computed(() => {
  if (isHovered.value) {
    // Priorité aux couleurs CSS au hover
    if (props.hoverCssColor) {
      return props.hoverCssColor;
    }
    if (props.hoverColor) {
      // Si c'est une couleur Ionic, la convertir en variable CSS
      if (isIonicColor(props.hoverColor)) {
        return `var(--ion-color-${props.hoverColor})`;
      }
      return props.hoverColor;
    }
  }

  // Couleur active (priorité sur la couleur par défaut)
  if (props.isActive) {
    if (props.activeCssColor) {
      return props.activeCssColor;
    }
    if (props.activeColor) {
      if (isIonicColor(props.activeColor)) {
        return `var(--ion-color-${props.activeColor})`;
      }
      return props.activeColor;
    }
  }

  // Priorité aux couleurs CSS
  if (props.cssColor) {
    return props.cssColor;
  }

  // Fallback sur color (Ionic ou custom)
  if (props.color) {
    // Si c'est une couleur Ionic et pas utilisé avec IonIcon, convertir en variable CSS
    if (isIonicColor(props.color)) {
      return `var(--ion-color-${props.color})`;
    }
    return props.color;
  }

  return 'var(--ion-text-color)';
});

// Pour IonIcon: utiliser la prop color seulement pour les couleurs Ionic
// IonIcon nécessite le nom de la couleur (ex: 'primary'), pas la variable CSS
const ionicColor = computed(() => {
  // Si cssColor est défini, ne pas utiliser la couleur Ionic native
  if (props.cssColor) {
    return undefined;
  }

  if (isHovered.value && props.hoverColor && isIonicColor(props.hoverColor)) {
    return props.hoverColor;
  }

  if (props.isActive && props.activeColor && isIonicColor(props.activeColor)) {
    return props.activeColor;
  }

  if (props.color && isIonicColor(props.color)) {
    return props.color;
  }

  return undefined;
});

// Style pour IonIcon avec couleur CSS personnalisée
const ionicStyle = computed(() => {
  const color = currentColor.value;
  const baseStyle = { ...containerStyle.value };

  // Si ce n'est pas une couleur Ionic, appliquer via CSS
  if (!isIonicColor(color)) {
    baseStyle.color = color;
  }

  return baseStyle;
});

const containerStyle = computed(() => {
  let style: Record<string, string>;

  // Récupérer la configuration de taille depuis iconUtils
  const iconSizeConfig = getIconConfigSize(props.name);

  if (iconSizeConfig) {
    // Utiliser la configuration de taille de iconUtils si disponible
    style = {
      width: iconSizeConfig.width?.toString() || sizeValue.value,
      height: iconSizeConfig.height?.toString() || sizeValue.value,
    };
  } else {
    // Style par défaut
    style = {
      width: sizeValue.value,
      height: sizeValue.value,
    };
  }

  // Ajouter le curseur pointer si clickable, animateOnClick ou animateOnHover
  if (props.clickable || props.animateOnClick || props.animateOnHover) {
    style.cursor = 'pointer';
  }

  // Pour les icônes Lottie, appliquer un filtre CSS seulement si aucune couleur parent n'est définie
  if (iconType.value === 'lottie' && !props.color) {
    style.filter = getColorFilter();
  }

  return style;
});

const getColorFilter = (): string => {
  // Filtres CSS basés sur le thème pour hériter de la couleur du parent
  if (isDarkTheme.value) {
    // En mode sombre, utiliser un filtre qui permet d'hériter de la couleur parent
    return 'brightness(0) saturate(100%) invert(1) brightness(1.2)';
  } else {
    // En mode clair, utiliser un filtre qui permet d'hériter de la couleur parent
    return 'brightness(0) saturate(100%) brightness(0.8)';
  }
};

const initLottieAnimation = () => {
  if (iconType.value === 'lottie' && lottieContainer.value && iconConfig.value) {
    // Nettoyage de l'animation précédente si elle existe
    if (animationInstance) {
      animationInstance.destroy();
    }

    // Configuration de l'animation selon le type d'interaction
    const animationConfig = {
      container: lottieContainer.value,
      renderer: 'svg' as const,
      loop: props.animateOnClick || props.animateOnHover ? false : props.loop,
      autoplay: props.animateOnClick || props.animateOnHover ? false : props.autoplay,
      animationData: iconConfig.value.data,
    };

    animationInstance = lottie.default.loadAnimation(animationConfig);

    // Ajouter les écouteurs d'événements pour les callbacks
    if (animationInstance) {
      animationInstance.addEventListener('complete', () => {
        emit('animationComplete');
      });

      animationInstance.addEventListener('enterFrame', () => {
        // Émis au début de l'animation
        if (animationInstance?.currentFrame === 0) {
          emit('animationStart');
        }
      });
    }
  }
};

const handleLottieClick = (event: MouseEvent) => {
  emit('click', event);

  if (props.animateOnClick && animationInstance) {
    executeAnimation(props.animationType, props.segments, animationDirection);
  }
};

const handleLottieMouseEnter = (event: MouseEvent) => {
  emit('mouseenter', event);

  if (props.animateOnHover && animationInstance) {
    executeAnimation(props.hoverAnimationType, props.hoverSegments, hoverAnimationDirection);
  }
};

const handleLottieMouseLeave = (event: MouseEvent) => {
  emit('mouseleave', event);

  if (props.animateOnHover && animationInstance && props.hoverAnimationType === 'reverse') {
    // Pour le hover reverse, on inverse la direction quand on quitte
    animationInstance.setDirection(-hoverAnimationDirection.value as any);
    animationInstance.play();
  }
};

// Gestionnaires d'événements pour le hover des couleurs
const handleMouseEnter = (event: MouseEvent) => {
  isHovered.value = true;
  emit('mouseenter', event);

  // Si c'est un élément Lottie, exécuter aussi l'animation
  if (iconType.value === 'lottie') {
    handleLottieMouseEnter(event);
  }
};

const handleMouseLeave = (event: MouseEvent) => {
  isHovered.value = false;
  emit('mouseleave', event);

  // Si c'est un élément Lottie, exécuter aussi l'animation
  if (iconType.value === 'lottie') {
    handleLottieMouseLeave(event);
  }
};

const executeAnimation = (
  type: 'play' | 'reverse' | 'segments',
  segments: [number, number] | undefined,
  direction: { value: number }
) => {
  if (!animationInstance) return;

  switch (type) {
    case 'play':
      // Animation simple
      animationInstance.setDirection(1);
      animationInstance.play();
      break;

    case 'reverse':
      // Animation avec changement de direction à chaque interaction
      animationInstance.setDirection(direction.value as any);
      animationInstance.play();
      direction.value = -direction.value;
      break;

    case 'segments':
      // Animation par segments
      if (segments) {
        animationInstance.playSegments(segments, true);
      } else {
        // Fallback : jouer toute l'animation
        animationInstance.goToAndStop(0, true);
        animationInstance.play();
      }
      break;

    default:
      animationInstance.goToAndStop(0, true);
      animationInstance.play();
  }
};

// Méthodes publiques pour contrôler l'animation depuis le parent
const playAnimation = () => {
  if (animationInstance) {
    animationInstance.play();
  }
};

const pauseAnimation = () => {
  if (animationInstance) {
    animationInstance.pause();
  }
};

const stopAnimation = () => {
  if (animationInstance) {
    animationInstance.stop();
  }
};

const resetAnimation = () => {
  if (animationInstance) {
    animationInstance.goToAndStop(0, true);
  }
};

const playSegments = (segments: [number, number], forceFlag?: boolean) => {
  if (animationInstance) {
    animationInstance.playSegments(segments, forceFlag);
  }
};

// Exposer les méthodes pour le composant parent
defineExpose({
  playAnimation,
  pauseAnimation,
  stopAnimation,
  resetAnimation,
  playSegments,
  animationInstance: computed(() => animationInstance),
});

// Watcher pour détecter les changements de thème via MutationObserver
let observer: MutationObserver | null = null;

onMounted(() => {
  if (iconType.value === 'lottie') {
    initLottieAnimation();
  }

  // Observer les changements de classe sur le body pour détecter les changements de thème
  observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const hasClass = document.body.classList.contains('dark');
        if (isDarkTheme.value !== hasClass) {
          isDarkTheme.value = hasClass;
        }
      }
    });
  });

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});

watch(
  () => props.name,
  () => {
    if (iconConfig.value.type === 'lottie') {
      initLottieAnimation();
    }
  }
);

watch(
  () => props.color,
  () => {
    // Le style réactif se mettra à jour automatiquement
  }
);

// Réinitialiser l'animation quand les props d'animation changent
watch(
  () => [
    props.animateOnClick,
    props.animateOnHover,
    props.animationType,
    props.hoverAnimationType,
    props.segments,
    props.hoverSegments,
  ],
  () => {
    if (iconType.value === 'lottie') {
      initLottieAnimation();
    }
  }
);

// Watcher pour le déclenchement automatique d'animation
watch(
  () => props.animationTrigger,
  (newValue) => {
    if (animationInstance && props.animationTrigger !== undefined) {
      // Pour les booléens, utiliser la valeur pour déterminer la direction
      if (typeof newValue === 'boolean') {
        if (newValue) {
          // true = animation vers l'avant
          animationInstance.setDirection(1);
        } else {
          // false = animation vers l'arrière
          animationInstance.setDirection(-1);
        }
        animationInstance.play();
      } else {
        // Pour les autres types, jouer l'animation selon le type défini
        executeAnimation(props.animationType, props.segments, animationDirection);
      }
    }
  },
  { immediate: false }
);

// Nettoyage lors de la destruction du composant
onBeforeUnmount(() => {
  if (animationInstance) {
    animationInstance.destroy();
  }
  if (observer) {
    observer.disconnect();
  }
});
</script>

<style scoped>
/* Assurer que les icônes utilisent la couleur par défaut du thème */
ion-icon {
  color: var(--ion-text-color);
}

/* Pour les icônes Lottie, appliquer la couleur via CSS */
div[ref='lottieContainer'] {
  color: inherit;
}

div[ref='lottieContainer'] svg {
  color: inherit;
}

/* Pour les autres types d'icônes */
i,
img {
  color: inherit;
}
</style>
